import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { canAccess } from '../utils/roleAccess';
import { getUserKey } from '../utils/userStorage';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiColumns, FiFolder, FiHome, FiLogOut, FiMail, FiMenu, FiSearch, FiSettings, FiUsers, FiX } from 'react-icons/fi';
import useIsMobile from '../hooks/useIsMobile';

const Sidebar = ({ openLogoutConfirm, openSearch }) => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useI18n();
  const { currentRole, currentUser } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const deadlineBadge = (() => {
    try {
      const tasks = JSON.parse(localStorage.getItem(getUserKey('kanban')) || '{}');
      const now = new Date();
      const fmt = d => `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
      const t1 = fmt(now);
      const tmr = new Date(now); tmr.setDate(tmr.getDate() + 1);
      const t2 = fmt(tmr);
      let n = 0;
      Object.values(tasks).forEach(b => Object.values(b).forEach(col => (col || []).forEach(card => {
        if (card.deadline?.date && (card.deadline.date === t1 || card.deadline.date === t2) && card.status !== 'Tayyor') n++;
      })));
      return n;
    } catch { return 0; }
  })();

  const allMenuItems = [
    { path: "/",          name: t('menu.dashboard'), icon: FiHome },
    { path: "/projects",  name: t('menu.projects'),  icon: FiFolder },
    { path: "/analytics", name: t('menu.analytics'), icon: FiColumns, badge: deadlineBadge > 0 ? deadlineBadge : null },
    { path: "/calendar",  name: t('menu.calendar'),  icon: FiCalendar },
    { path: "/contact",   name: t('menu.contact'),   icon: FiMail },
    { path: "/admin",     name: t('menu.admin'),      icon: FiUsers },
    { path: "/profile",   name: t('menu.settings'),  icon: FiSettings },
  ];
  const menuItems = allMenuItems.filter(item => canAccess(currentRole, item.path));

  const roleColors = {
    'Admin': '#ef4444', 'SMM Menejer': '#3b82f6', 'Dizayner': '#8b5cf6',
    'Montajor': '#f97316', 'Kopirayter': '#22c55e',
    'VFX Mutaxasis': '#06b6d4', 'AI Mutaxasis': '#ec4899',
  };
  const roleColor = roleColors[currentRole] || '#3b82f6';
  const displayName = currentUser ? currentUser.name : 'Admin';
  const displayRole = currentRole;

  /* ─── MOBILE: bottom nav ─── */
  if (isMobile) {
    const bg   = isLight ? 'rgba(255,255,255,0.95)' : 'rgba(15,23,42,0.97)';
    const bord = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';

    return (
      <>
        {/* Slide-out drawer */}
        <AnimatePresence>
          {drawerOpen && (
            <>
              <motion.div key="overlay" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                onClick={() => setDrawerOpen(false)}
                style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)' }} />
              <motion.div key="drawer" initial={{ x:'-100%' }} animate={{ x:0 }} exit={{ x:'-100%' }}
                transition={{ type:'spring', stiffness:340, damping:34 }}
                style={{ position:'fixed', top:0, left:0, bottom:0, width:'min(280px, 80vw)', zIndex:9999,
                  background: isLight ? '#ffffff' : '#0f172a',
                  borderRight: `1px solid ${bord}`,
                  boxShadow:'8px 0 40px rgba(0,0,0,0.3)',
                  display:'flex', flexDirection:'column', padding:'20px 14px',
                  paddingBottom:'calc(20px + env(safe-area-inset-bottom))' }}>

                {/* Logo */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'32px', paddingLeft:'8px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3px' }}>
                      <div style={{ width:'9px', height:'9px', backgroundColor:'#f25022' }} />
                      <div style={{ width:'9px', height:'9px', backgroundColor:'#7fba00' }} />
                      <div style={{ width:'9px', height:'9px', backgroundColor:'#00a4ef' }} />
                      <div style={{ width:'9px', height:'9px', backgroundColor:'#ffb900' }} />
                    </div>
                    <span style={{ fontSize:'18px', fontWeight:800, color: isLight ? '#0f172a' : '#f8fafc' }}>SMM CRM</span>
                  </div>
                  <button onClick={() => setDrawerOpen(false)} aria-label={t('analytics.buttons.cancel')}
                    style={{ background:'none', border:'none', cursor:'pointer',
                      color: isLight ? '#64748b' : '#94a3b8', padding:'4px', display:'grid', placeItems:'center' }}><FiX size={19} /></button>
                </div>

                {/* Nav items */}
                <nav style={{ display:'flex', flexDirection:'column', gap:'6px', flex:1 }}>
                  {menuItems.map(item => {
                    const MenuIcon = item.icon;
                    return (
                      <NavLink key={item.path} to={item.path} onClick={() => setDrawerOpen(false)}
                        style={({ isActive }) => ({
                          display:'flex', alignItems:'center', gap:'14px',
                          padding:'13px 16px', borderRadius:'13px', textDecoration:'none',
                          fontSize:'15px', fontWeight:600,
                          color: isActive ? '#fff' : isLight ? '#475569' : '#cbd5e1',
                          background: isActive ? 'var(--accent-color)' : 'transparent',
                          boxShadow: isActive ? '0 4px 15px rgba(var(--accent-color-rgb),0.4)' : 'none',
                          transition:'all 0.2s',
                        })}>
                        <MenuIcon size={18} strokeWidth={1.8} aria-hidden="true" />
                        <span style={{ flex:1 }}>{item.name}</span>
                        {item.badge && (
                          <span style={{ background:'#ef4444', color:'#fff', borderRadius:'10px', padding:'1px 7px', fontSize:'11px', fontWeight:700 }}>
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                  {openSearch && (
                    <button type="button" onClick={() => { setDrawerOpen(false); openSearch(); }}
                      style={{ display:'flex', alignItems:'center', gap:'14px', padding:'13px 16px', borderRadius:'13px', border:'1px dashed rgba(255,255,255,0.2)', background:'transparent', color: isLight ? '#94a3b8' : '#64748b', cursor:'pointer', fontSize:'15px', fontFamily:'inherit', fontWeight:600 }}>
                      <FiSearch size={18} strokeWidth={1.8} aria-hidden="true" />
                      {t('menu.search')}
                    </button>
                  )}
                </nav>

                {/* User info */}
                <div style={{ padding:'12px 14px', borderRadius:'13px', marginBottom:'8px',
                  background: roleColor+'15', border:`1px solid ${roleColor}30` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div style={{ width:'36px', height:'36px', borderRadius:'10px', flexShrink:0,
                      background: roleColor+'25', display:'flex', alignItems:'center',
                      justifyContent:'center', fontSize:'15px', fontWeight:800, color:roleColor }}>
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth:0 }}>
                      <p style={{ margin:0, fontWeight:700, fontSize:'13px',
                        color: isLight ? '#0f172a' : '#f8fafc',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {displayName}
                      </p>
                      <span style={{ fontSize:'11px', fontWeight:700, color:roleColor }}>
                        {displayRole}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Logout */}
                <motion.button type="button" whileTap={{ scale:0.97 }}
                  onClick={() => { setDrawerOpen(false); openLogoutConfirm(); }}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
                    width:'100%', padding:'13px', background:'rgba(239,68,68,0.1)',
                    color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)',
                    borderRadius:'13px', cursor:'pointer', fontSize:'15px', fontWeight:700,
                    fontFamily:'inherit', marginTop:'4px' }}>
                  <FiLogOut size={17} aria-hidden="true" /> {t('logout')}
                </motion.button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Bottom navigation bar */}
        <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:9000,
          background: bg, borderTop:`1px solid ${bord}`,
          backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)',
          display:'flex', alignItems:'center', justifyContent:'space-around',
          padding:'6px 0', paddingBottom:'env(safe-area-inset-bottom, 6px)',
          boxShadow:'0 -4px 24px rgba(0,0,0,0.15)' }}>

          {/* Hamburger (menu) */}
          <button onClick={() => setDrawerOpen(true)} type="button"
            style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'3px',
              background:'none', border:'none', cursor:'pointer', padding:'6px 10px',
              color: isLight ? '#64748b' : '#94a3b8', fontFamily:'inherit', minWidth:'52px' }}>
            <FiMenu size={20} strokeWidth={1.8} aria-hidden="true" />
            <span style={{ fontSize:'10px', fontWeight:600 }}>Menyu</span>
          </button>

          {/* Main 4 nav items (excluding profile) */}
          {menuItems.slice(0, 4).map(item => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const BottomNavIcon = item.icon;
            return (
              <NavLink key={item.path} to={item.path}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'3px',
                  textDecoration:'none', padding:'6px 10px', minWidth:'52px',
                  color: isActive ? 'var(--accent-color)' : isLight ? '#64748b' : '#94a3b8',
                  position:'relative' }}>
                {isActive && (
                  <motion.div layoutId="bottomNavIndicator"
                    style={{ position:'absolute', top:'-1px', left:'50%', transform:'translateX(-50%)',
                      width:'24px', height:'3px', borderRadius:'0 0 4px 4px',
                      background:'var(--accent-color)' }} />
                )}
                <BottomNavIcon size={20} strokeWidth={1.8} aria-hidden="true" />
                <span style={{ fontSize:'10px', fontWeight: isActive ? 700 : 600,
                  color: isActive ? 'var(--accent-color)' : 'inherit' }}>
                  {item.name.split(' ')[0]}
                </span>
              </NavLink>
            );
          })}

          {/* Profile */}
          <NavLink to="/profile"
            style={({ isActive }) => ({
              display:'flex', flexDirection:'column', alignItems:'center', gap:'3px',
              textDecoration:'none', padding:'6px 10px', minWidth:'52px',
              color: isActive ? 'var(--accent-color)' : isLight ? '#64748b' : '#94a3b8',
              position:'relative',
            })}>
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.div layoutId="bottomNavIndicatorProfile"
                    style={{ position:'absolute', top:'-1px', left:'50%', transform:'translateX(-50%)',
                      width:'24px', height:'3px', borderRadius:'0 0 4px 4px',
                      background:'var(--accent-color)' }} />
                )}
                <FiSettings size={20} strokeWidth={1.8} aria-hidden="true" />
                <span style={{ fontSize:'10px', fontWeight: isActive ? 700 : 600 }}>
                  {t('menu.settings').split(' ')[0]}
                </span>
              </>
            )}
          </NavLink>
        </div>
      </>
    );
  }

  /* ─── DESKTOP: sidebar ─── */
  return (
    <div className="glass"
      style={{ width:'260px', height:'calc(100vh - 40px)', margin:'20px 0 20px 20px',
        padding:'30px 20px', display:'flex', flexDirection:'column',
        justifyContent:'space-between', borderRadius:'24px', flexShrink:0,
        border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
        color: isLight ? '#0f172a' : '#fff',
        transition:'all 0.3s ease', boxShadow:'0 8px 32px 0 rgba(0,0,0,0.15)' }}>

      <div>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'40px', paddingLeft:'10px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3px' }}>
            <div style={{ width:'10px', height:'10px', backgroundColor:'#f25022' }} />
            <div style={{ width:'10px', height:'10px', backgroundColor:'#7fba00' }} />
            <div style={{ width:'10px', height:'10px', backgroundColor:'#00a4ef' }} />
            <div style={{ width:'10px', height:'10px', backgroundColor:'#ffb900' }} />
          </div>
          <span style={{ fontSize:'20px', fontWeight:'bold', letterSpacing:'1px' }}>SMM CRM</span>
        </div>

        <nav style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {menuItems.map((item) => {
            const MobileNavIcon = item.icon;
            return (
              <NavLink key={item.path} to={item.path}
                style={({ isActive }) => ({
                  display:'flex', alignItems:'center', gap:'15px',
                  padding:'14px 20px', borderRadius:'12px', textDecoration:'none',
                  fontSize:'16px', fontWeight:'500',
                  color: isActive ? '#fff' : (isLight ? '#475569' : '#cbd5e1'),
                  background: isActive ? 'var(--accent-color)' : 'transparent',
                  boxShadow: isActive ? '0 4px 15px rgba(var(--accent-color-rgb),0.4)' : 'none',
                  transition:'all 0.2s ease',
                })}>
                <MobileNavIcon size={18} strokeWidth={1.8} aria-hidden="true" />
                <span style={{ flex:1 }}>{item.name}</span>
                {item.badge && (
                  <span style={{ background:'#ef4444', color:'#fff', borderRadius:'10px', padding:'1px 7px', fontSize:'11px', fontWeight:700, lineHeight:'18px' }}>
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}

          {openSearch && (
            <button type="button" onClick={openSearch}
              style={{ display:'flex', alignItems:'center', gap:'15px', padding:'12px 20px', borderRadius:'12px', border: isLight ? '1px dashed #cbd5e1' : '1px dashed rgba(255,255,255,0.15)', background:'transparent', color: isLight ? '#94a3b8' : '#64748b', cursor:'pointer', fontSize:'14px', fontFamily:'inherit', marginTop:'4px', transition:'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-color)'; e.currentTarget.style.color = 'var(--accent-color)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = isLight ? '#94a3b8' : '#64748b'; }}>
              <FiSearch size={17} strokeWidth={1.8} aria-hidden="true" />
              <span style={{ flex:1 }}>{t('menu.search')}</span>
              <kbd style={{ padding:'2px 5px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.07)', borderRadius:'4px', fontSize:'10px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)' }}>Ctrl K</kbd>
            </button>
          )}
        </nav>
      </div>

      {/* User info card */}
      <div style={{ marginBottom:'10px', padding:'12px 14px', borderRadius:'12px',
        background: roleColor+'12', border:`1px solid ${roleColor}25` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          <div style={{ width:'34px', height:'34px', borderRadius:'10px', flexShrink:0,
            background: roleColor+'25', display:'flex', alignItems:'center',
            justifyContent:'center', fontSize:'14px', fontWeight:800, color:roleColor }}>
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth:0 }}>
            <p style={{ margin:0, fontWeight:700, fontSize:'13px',
              color: isLight ? '#0f172a' : '#f8fafc',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {displayName}
            </p>
            <span style={{ fontSize:'11px', fontWeight:700, color:roleColor }}>
              {displayRole}
            </span>
          </div>
        </div>
      </div>

      <motion.button type="button" whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
        onClick={openLogoutConfirm}
        style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
          width:'100%', padding:'14px', background:'rgba(239,68,68,0.1)',
          color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)',
          borderRadius:'12px', cursor:'pointer', fontSize:'16px', fontWeight:'bold',
          transition:'all 0.2s ease', fontFamily:'inherit' }}>
        <FiLogOut size={17} aria-hidden="true" /> {t('logout')}
      </motion.button>
    </div>
  );
};

export default Sidebar;
